import { Spin } from 'antd';
import classnames from 'classnames';
import { flatten } from 'lodash-es';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import {
  Calendar,
  Components as RbcComponents,
  DateRangeFormatFunction,
  Event as RbcEvent,
  Formats as RbcFormats,
  dayjsLocalizer,
  View as RbcView,
  Views as RbcViews,
  ViewsProps as RbcViewsProps,
} from 'react-big-calendar';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import dayjs from 'dayjs';
import { DATE_FORMAT_PATTERN, TimezoneType } from '../../../../../../lib/utils/date-handling/DateTime.types';
import {
  ICalendarEvent,
  ICleaningPlanEvent,
  IWorkIntervalLocal,
} from '../../../../cleaning/interfaces/CleaningPlan.types';
import { useWorkIntervalDataRequestGuard } from '../../../../cleaning/contexts/useWorkIntervalDataRequestGuard';
import { CalendarUtils } from '../../../../cleaning/utils/CalendarUtils';
import {
  fullscreenCappedOverlapDayLayoutAlgorithm,
  inlineCappedOverlapDayLayoutAlgorithm,
} from '../../../../cleaning/utils/cappedOverlapDayLayoutAlgorithm';
import { CleaningPlanUtils } from '../../../../cleaning/utils/CleaningPlanUtils';
import * as siteDetailsSelectors from '../../state/siteDetailsPanelSelectors';
import { CleaningPlanEvent } from '../CleaningPlanEvent/CleaningPlanEvent';
import { StyledCleaningPlan } from './CleaningPlan.styles';
import { DateTime } from 'lib/utils/date-handling/DateTime';
import { SecondaryButton } from 'lib/components/Button/SecondaryButton/SecondaryButton';
import { TextButton } from 'lib/components/Button/TextButton/TextButton';
import { SvgIcon } from 'lib/components/SvgIcon/SvgIcon';
import {
  IShowAddWorkIntervalModalAction,
  SiteModalsActions,
} from 'app/modules/site-management/modals/state/siteModalsActions';
import { Site } from 'app/cross-cutting-concerns/communication/interfaces/am-api-graphql';
import { PermissionGuard } from 'app/cross-cutting-concerns/authentication/components/PermissionGuard/PermissionGuard';
import { Permission } from 'config/permissions';

const localizer = dayjsLocalizer(dayjs);

const CALENDAR_OPTIONS: {
  views: RbcViewsProps<RbcEvent, Record<string, unknown>>;
  defaultView: RbcView;
  dayFormat: string;
} = {
  views: [RbcViews.WEEK],
  defaultView: RbcViews.WEEK,
  dayFormat: 'ddd',
};

interface CleaningPlanProps {
  site: Site;
}

export const CleaningPlan = ({ site: _site }: CleaningPlanProps): JSX.Element => {
  const { t, i18n } = useTranslation();
  const [defaultDate, setDefaultDate] = useState<Date>();
  const [isFullscreen, setIsFullscreen] = useState(false);
  const fullscreenContainerRef = useRef<HTMLDivElement>(null);
  const dispatch = useDispatch();
  const workIntervals = useSelector(siteDetailsSelectors.selectWorkIntervals);
  const areCleaningPlansLoading = useSelector(siteDetailsSelectors.selectAreWorkIntervalsLoading);

  const calendarEvents: ICalendarEvent[] = useMemo(() => {
    const cleaningPlanEvents: ICleaningPlanEvent[] = flatten(
      (workIntervals ?? []).map((workIntervalLocal: IWorkIntervalLocal): ICleaningPlanEvent[] =>
        CleaningPlanUtils.convertWorkIntervalToCleaningPlanEvents(workIntervalLocal, TimezoneType.LOCAL)
      )
    );

    return cleaningPlanEvents.map(datum => CalendarUtils.convertCleaningPlanEventToCalendarEvent(datum));
  }, [workIntervals]);

  // Call hook to ensure that data needed by this component is loaded higher up in the component hierarchy
  useWorkIntervalDataRequestGuard();

  useEffect(() => {
    setDefaultDate(DateTime.today());
  }, []);

  useEffect(() => {
    if (!isFullscreen) {
      return undefined;
    }

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    fullscreenContainerRef.current?.focus();

    const handleKeyDown = (event: KeyboardEvent): void => {
      if (event.key === 'Escape') {
        setIsFullscreen(false);
      }
    };

    document.addEventListener('keydown', handleKeyDown);

    return (): void => {
      document.body.style.overflow = previousOverflow;
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [isFullscreen]);

  const handleToggleFullscreen = useCallback((): void => {
    setIsFullscreen(currentlyFullscreen => !currentlyFullscreen);
  }, []);

  const convertEventTimeRangeFormat: DateRangeFormatFunction = (range): string => {
    const start = DateTime.formatDateByLocale(
      i18n.language,
      range.start.toISOString(),
      DATE_FORMAT_PATTERN.TIME_WITHOUT_SECOND
    );

    const end = DateTime.formatDateByLocale(
      i18n.language,
      range.end.toISOString(),
      DATE_FORMAT_PATTERN.TIME_WITHOUT_SECOND
    );

    return `${start} - ${end}`;
  };

  const calendarI18nDateFormats: RbcFormats = {
    ...t('siteDetails.cleaningPlan.reactBigCalendarFormats', { returnObjects: true }),
    eventTimeRangeFormat: convertEventTimeRangeFormat,
  };

  const calendarComponents: RbcComponents<ICalendarEvent, Record<string, unknown>> = useMemo(
    () => ({
      eventWrapper: (props): React.ReactElement => <CleaningPlanEvent {...props} />,
    }),
    []
  );

  const scrollToTime = useMemo(() => CalendarUtils.getInitialTimeFromCalendarEvents(calendarEvents), [calendarEvents]);

  const cleaningPlan = (
    <StyledCleaningPlan
      ref={fullscreenContainerRef}
      className={classnames('cleaning-plan', { 'cleaning-plan--fullscreen': isFullscreen })}
      tabIndex={isFullscreen ? -1 : undefined}
    >
      <div className="cleaning-plan__header">
        <h3 tabIndex={0} className="cleaning-plan__title">
          {t('siteDetails.cleaningPlan.title')}
        </h3>
        <TextButton
          size="s"
          iconPosition="start"
          icon={<SvgIcon name={isFullscreen ? 'shrink' : 'expand'} />}
          onClick={handleToggleFullscreen}
          className="cleaning-plan__fullscreen-button"
        >
          {t(isFullscreen ? 'siteDetails.cleaningPlan.exitFullScreen' : 'siteDetails.cleaningPlan.fullScreen')}
        </TextButton>
      </div>
      <div className="cleaning-plan__calendar-wrapper">
        <Spin spinning={areCleaningPlansLoading} wrapperClassName="cleaning-plan__spin">
          <Calendar
            className="cleaning-plan__calendar"
            defaultDate={defaultDate}
            defaultView={CALENDAR_OPTIONS.defaultView}
            events={calendarEvents}
            localizer={localizer}
            views={CALENDAR_OPTIONS.views}
            components={calendarComponents}
            toolbar={false}
            dayLayoutAlgorithm={
              isFullscreen ? fullscreenCappedOverlapDayLayoutAlgorithm : inlineCappedOverlapDayLayoutAlgorithm
            }
            formats={{ ...calendarI18nDateFormats, dayFormat: CALENDAR_OPTIONS.dayFormat }}
            culture={i18n.language}
            scrollToTime={scrollToTime}
          />
        </Spin>

        <PermissionGuard requiredPermissions={[Permission.Site.WorkInterval.UPDATE]}>
          <div className="cleaning-plan__footer">
            <SecondaryButton
              size="s"
              onClick={(): IShowAddWorkIntervalModalAction => dispatch(SiteModalsActions.showAddWorkIntervalModal())}
              className="cleaning-plan__add-work-interval-button"
              key="cleaning-plan__add-work-interval-button"
            >
              {t('siteDetails.cleaningPlan.addWorkInterval')}
            </SecondaryButton>
          </div>
        </PermissionGuard>
      </div>
    </StyledCleaningPlan>
  );

  if (!isFullscreen) {
    return cleaningPlan;
  }

  // Portal out of SiteDetailsDrawer: its content wrapper is transformed, so
  // position:fixed would otherwise cover the drawer instead of the viewport.
  return (
    <>
      <StyledCleaningPlan className="cleaning-plan cleaning-plan--placeholder" aria-hidden />
      {createPortal(cleaningPlan, document.body)}
    </>
  );
};
